/**
 * Local storage utility for persisting data across sessions
 */

export const STORAGE_KEYS = {
  FEEDBACK_CAPTURED_AADT: 'feedback_captured_aadt',
  FEEDBACK_FORECAST: 'feedback_forecast',
  LAST_WORKSPACE_ID: 'last_workspace_id',
  LAST_ZONE_ID: 'last_zone_id',
  LAST_LOCATION_ID: 'last_location_id',
  LAST_DEAL_ID: 'last_deal_id',
  COMPUTE_PREFERENCES: 'compute_preferences',
} as const;

export type StorageKey = (typeof STORAGE_KEYS)[keyof typeof STORAGE_KEYS];

type FeedbackRecord = Record<string, boolean>;

/**
 * Fired on `setStorageItem`/`removeStorageItem` so same-tab readers can react
 * — the native `storage` event only fires in *other* tabs/windows.
 */
const STORAGE_CHANGE_EVENT = 'app:storage-change';

interface StorageChangeEventDetail {
  key: StorageKey;
}

/**
 * Get an item from localStorage
 * @param key - The storage key
 * @returns The parsed value or null if not found
 */
export function getStorageItem<T>(key: StorageKey): T | null {
  try {
    const item = localStorage.getItem(key);
    if (item === null) return null;
    return JSON.parse(item) as T;
  } catch {
    return null;
  }
}

/**
 * Set an item in localStorage
 * @param key - The storage key
 * @param value - The value to store (will be JSON stringified)
 */
export function setStorageItem<T>(key: StorageKey, value: T): void {
  try {
    localStorage.setItem(key, JSON.stringify(value));
    window.dispatchEvent(
      new CustomEvent<StorageChangeEventDetail>(STORAGE_CHANGE_EVENT, { detail: { key } }),
    );
  } catch {
    console.warn(`Failed to save to localStorage: ${key}`);
  }
}

/**
 * Remove an item from localStorage
 * @param key - The storage key
 */
export function removeStorageItem(key: StorageKey): void {
  try {
    localStorage.removeItem(key);
    window.dispatchEvent(
      new CustomEvent<StorageChangeEventDetail>(STORAGE_CHANGE_EVENT, { detail: { key } }),
    );
  } catch {
    console.warn(`Failed to remove from localStorage: ${key}`);
  }
}

/**
 * Subscribe to changes of a single storage key, same-tab (`setStorageItem`/
 * `removeStorageItem`) and cross-tab (native `storage` event). Pair with
 * `useSyncExternalStore` to keep a component's read of `localStorage` live.
 */
export function subscribeToStorageItem(key: StorageKey, callback: () => void): () => void {
  function handleAppChange(event: Event) {
    if (event instanceof CustomEvent && (event.detail as StorageChangeEventDetail)?.key === key) {
      callback();
    }
  }
  function handleNativeStorage(event: StorageEvent) {
    if (event.key === key) callback();
  }
  window.addEventListener(STORAGE_CHANGE_EVENT, handleAppChange);
  window.addEventListener('storage', handleNativeStorage);
  return () => {
    window.removeEventListener(STORAGE_CHANGE_EVENT, handleAppChange);
    window.removeEventListener('storage', handleNativeStorage);
  };
}

/**
 * Get the feedback value for a project
 * @param key - The feedback storage key
 * @param projectId - The project ID to check
 * @returns The feedback value (true/false) or null if no feedback was given
 */
export function getProjectFeedback(key: StorageKey, projectId: string): boolean | null {
  const feedbackRecord = getStorageItem<FeedbackRecord>(key);
  if (feedbackRecord === null) return null;
  const value = feedbackRecord[projectId];
  return typeof value === 'boolean' ? value : null;
}

/**
 * Save feedback for a project
 * @param key - The feedback storage key
 * @param projectId - The project ID
 * @param feedback - The feedback value (true = positive, false = negative)
 */
export function setProjectFeedback(key: StorageKey, projectId: string, feedback: boolean): void {
  const feedbackRecord = getStorageItem<FeedbackRecord>(key) ?? {};
  feedbackRecord[projectId] = feedback;
  setStorageItem(key, feedbackRecord);
}
